import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { LoggedAction, LoggedEntityType } from "lib/types/graphQLEnums";
import { NoticeDetails } from "../notices/useNotice";
import { NoticesDto } from "../notices/useNotices";
import { updateCachedNotices } from "../notices/utils";
import { useLogActivity } from "./useLogActivity";

/**
 * Returns a function that records the current user opening an entity, for usage reports and
 * "seen by". A notice also shows the user in its cached seen-by list straight away.
 */
export function useLogEntityView() {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  const { mutate } = useLogActivity({
    onSuccess: (_data, { input }) => {
      if (!currentUser || input.entityType !== LoggedEntityType.Notice || !input.entityId) {
        return;
      }
      updateCachedNotices({
        queryClient,
        lookup: { type: "guid", guid: input.entityId },
        updateNoticesRow: (r) => markAsSeenByMe<NoticesDto>(r, r.seenByUsers, currentUser),
        updateNoticeQuery: (r) => markAsSeenByMe<NoticeDetails>(r, r.seenByUsers, currentUser),
      });
    },
  });

  return useCallback(
    (entityType: LoggedEntityType, entityId: string) =>
      mutate({ input: { action: LoggedAction.View, entityType, entityId } }),
    [mutate],
  );
}

/** Records one view each time the page or drawer shows a different entity. */
export function useLogEntityViewOnOpen(entityType: LoggedEntityType, entityId: string | undefined) {
  const logEntityView = useLogEntityView();

  useEffect(() => {
    if (entityId) {
      logEntityView(entityType, entityId);
    }
    // logEntityView changes with each mutation's state, so it would record a view every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);
}

function markAsSeenByMe<T>(
  notice: T,
  seenByUsers: { firstName: string; guid: string }[],
  currentUser: CurrentUserData,
): T {
  if (seenByUsers.find((user) => user.guid === currentUser.guid)) {
    return notice;
  }
  return {
    ...notice,
    seenByUsers: [
      ...seenByUsers,
      { firstName: currentUser.firstName || "", guid: currentUser.guid },
    ],
  };
}
